import DirectionsBoatOutlinedIcon from '@mui/icons-material/DirectionsBoatOutlined';

import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useEffect, useState } from 'react';

import {
  addShip,
  getLpSuppliers,
  getShip,
} from '../../../../../features/master-data';

import { StorageKey } from '../../../../../shared/auth/DataService';

import ScenarioModeAlert from '../../../../../shared/components/display/ScenarioModeAlert';

import MBModal from '../../../../../shared/components/modals/MBModal';

import useDateFormat from '../../../../../shared/hooks/useDateFormat';

import useScenarioMode from '../../../../../shared/hooks/useScenarioMode';

import {
  normalizeImo,
  parseImoDigits,
} from '../../../../../shared/utils/formatImo';

import {
  getVoyageWithShipments,
  updateVoyage,
} from '../../../services/VoyageRepository';

import { useReportingCoordination } from '../../../store';

/**

 * Modal that lets a user create (or reuse) a ship and assign it to a TBN voyage.

 *

 * @param {{ open: boolean, voyage: object|null, onClose: () => void,

 *           setSnackbarOpen: Function, setSnackbarMessage: Function, setSnackbarSeverity: Function }} props

 */

const AddShipModal = ({
  open,

  voyage,

  onClose,

  setSnackbarOpen,

  setSnackbarMessage,

  setSnackbarSeverity,
}) => {
  const { fetchAllData } = useReportingCoordination();

  const { formatDate } = useDateFormat();

  const { isScenarioActive, scenarioLabel } = useScenarioMode();

  // ── Form state ──

  const [imoDigits, setImoDigits] = useState('');

  const [shipName, setShipName] = useState('');

  const [irs, setIrs] = useState('');

  const [capacity, setCapacity] = useState('');

  const [supplier, setSupplier] = useState(null);

  const [suppliers, setSuppliers] = useState([]);

  // ── UI state ──

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isCheckingImo, setIsCheckingImo] = useState(false);

  const [existingShip, setExistingShip] = useState(null); // non-null when IMO already in DB

  const [error, setError] = useState('');

  // ── Load suppliers on mount ──

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    getLpSuppliers({ isShippingCompany: true })
      .then((data) => {
        if (!cancelled) setSuppliers(data || []);
      })

      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [open]);

  // ── Prefill from voyage when modal opens ──

  useEffect(() => {
    if (!open || !voyage) return;

    // Reset form

    setExistingShip(null);

    setError('');

    setShipName('');

    setIrs('');

    setCapacity('');

    setIsSubmitting(false);

    // Try to parse IMO from generalRemarks

    const parsed = parseImoDigits(voyage.generalRemarks);

    setImoDigits(parsed);

    // Preselect carrier as supplier when available

    if (voyage.carrier) {
      setSupplier({
        supplierNo: voyage.carrier.supplierNo,

        supplierName: voyage.carrier.supplierName || voyage.carrier.supplierNo,
      });
    } else {
      setSupplier(null);
    }
  }, [open, voyage]);

  // ── Proactive IMO lookup when digits reach 7 ──

  useEffect(() => {
    if (!open || imoDigits.length !== 7) {
      setIsCheckingImo(false);

      return;
    }

    let cancelled = false;

    const normalized = normalizeImo(imoDigits);

    if (!normalized) return;

    setIsCheckingImo(true);

    getShip(normalized)
      .then((found) => {
        if (!cancelled && found && found.imo) {
          setExistingShip(found);
        }
      })

      .catch(() => {
        // 404 / not found — ship does not exist yet

        if (!cancelled) setExistingShip(null);
      })

      .finally(() => {
        if (!cancelled) setIsCheckingImo(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, imoDigits]);

  // ── Helpers ──

  const handleImoChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 7);

    setImoDigits(digits);

    setExistingShip(null);

    setError('');
  };

  const validate = () => {
    if (imoDigits.length !== 7) return 'IMO must be exactly 7 digits.';

    // An existing ship already carries its own supplier.
    if (existingShip) return null;

    if (!shipName.trim()) return 'Ship Name is required.';

    if (!supplier) return 'Supplier is required.';

    return null;
  };

  // ── Submit orchestration ──

  const handleSubmit = async () => {
    const validationError = validate();

    if (validationError) {
      setError(validationError);

      return;
    }

    setError('');

    setIsSubmitting(true);

    const normalized = normalizeImo(imoDigits);

    if (!normalized) {
      setError('Invalid IMO format.');

      setIsSubmitting(false);

      return;
    }

    const emea = localStorage.getItem(StorageKey.userInfo_sub);

    let shipWasCreated = false;

    try {
      // ── Step 1: Check if ship already exists ──

      let shipExists = existingShip;

      if (!shipExists) {
        try {
          const found = await getShip(normalized);

          if (found && found.imo) {
            setExistingShip(found);

            shipExists = found;
          }
        } catch {
          // 404 / not found => ship does not exist, continue to create
        }
      }

      // ── Step 2: Create ship if needed ──

      if (!shipExists) {
        try {
          await addShip({
            imo: normalized,

            name: shipName.trim(),

            irs: irs.trim() || null,

            capacity: capacity ? Number(capacity) : null,

            supplier: supplier ? { supplierNo: supplier.supplierNo } : null,

            lastUpdatedBy: { emea },
          });

          shipWasCreated = true;
        } catch (createErr) {
          const msg = createErr?.message || 'Unknown error creating ship.';

          setError(`Ship creation failed: ${msg}`);

          setIsSubmitting(false);

          return;
        }
      }

      // ── Step 3: Fetch latest voyage data and build update payload ──

      let latestVoyage;

      try {
        latestVoyage = await getVoyageWithShipments(voyage.id);
      } catch {
        const phase = shipWasCreated
          ? 'Ship was created, but failed to fetch voyage data for update.'
          : 'Failed to fetch voyage data for update.';

        setError(phase);

        setIsSubmitting(false);

        return;
      }

      const effectiveSupplier = shipExists?.supplier || supplier;

      const voyagePayload = {
        id: voyage.id,

        departurePort: {
          unLocode: latestVoyage.departurePort?.unLocode,
        },

        departureCountry: {
          countryCode: latestVoyage.departureCountry?.countryCode,
        },

        destinationPort: {
          unLocode: latestVoyage.destinationPort?.unLocode,
        },

        destinationCountry: {
          countryCode: latestVoyage.destinationCountry?.countryCode,
        },

        shippingCompany: effectiveSupplier?.supplierNo
          ? { supplierNo: effectiveSupplier.supplierNo }
          : latestVoyage.ship?.supplier
            ? { supplierNo: latestVoyage.ship.supplier.supplierNo }
            : null,

        portOperator: latestVoyage.portOperator
          ? { supplierNo: latestVoyage.portOperator.supplierNo }
          : null,

        lastUpdatedBy: { emea },

        carrier: latestVoyage.carrier
          ? { supplierNo: latestVoyage.carrier.supplierNo }
          : null,

        ship: { imo: normalized },

        voyageNumber: latestVoyage.voyageNumber,

        initialETD: latestVoyage.initialETD || null,

        initialETA: latestVoyage.initialETA || null,

        actualETD: latestVoyage.actualETD || null,

        actualETA: latestVoyage.actualETA || null,

        slotConfirmed: latestVoyage.slotConfirmed ?? false,

        generalRemarks: (latestVoyage.generalRemarks || '')

          .replace(/IMO\s*\d{7}\s*does not exist\s*(\|\s*)?/gi, '')

          .replace(/\d{7}\s*does not exist\s*(\|\s*)?/gi, '')

          .replace(/IMO\s*\d{7}/gi, '')

          .replace(/\d{7}/, '')

          .replace(/\s{2,}/g, ' ')

          .trim(),

        cancelled: latestVoyage.cancelled ?? false,
      };

      const shipmentData = (latestVoyage.shipments || []).map((s) => ({
        id: s.id || null,

        voyage: { id: voyage.id },

        lastUpdatedBy: { emea },

        market: { id: s.market?.id },

        effectiveShipped: s.effectiveShipped || 0,

        finalAgreement: s.finalAgreement || 0,

        firstShippingLineOffer: s.firstShippingLineOffer || 0,

        adjustedPORequest: s.adjustedPORequest || 0,
      }));

      // ── Step 4: Update voyage ──

      try {
        await updateVoyage(voyage.id, {
          voyage: voyagePayload,

          shipments: shipmentData,
        });
      } catch (updateErr) {
        const action = shipWasCreated
          ? 'Ship was created successfully, but voyage update failed.'
          : shipExists
            ? 'Existing ship kept, but voyage update failed.'
            : 'Voyage update failed.';

        const detail = updateErr?.message || '';

        setError(`${action}${detail ? ` (${detail})` : ''}`);

        setIsSubmitting(false);

        return;
      }

      // ── Step 5: Success ──

      const successMsg = shipWasCreated
        ? `Ship ${normalized} created and voyage updated.`
        : `Existing ship ${normalized} reused and voyage updated.`;

      setSnackbarMessage(successMsg);

      setSnackbarSeverity('success');

      setSnackbarOpen(true);

      fetchAllData();

      onClose();
    } catch (err) {
      setError(err?.message || 'An unexpected error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!voyage) return null;

  return (
    <MBModal
      open={open}
      onClose={onClose}
      disableClose={isSubmitting}
      maxWidth='sm'
      title={
        isScenarioActive ? (
          <Stack direction='row' alignItems='center' spacing={1}>
            <span>Add Ship to Voyage</span>

            <Chip
              icon={<ScienceOutlinedIcon />}
              label={scenarioLabel('Scenario Mode')}
              color='warning'
              size='small'
            />
          </Stack>
        ) : (
          'Add Ship to Voyage'
        )
      }
      titleIcon={<DirectionsBoatOutlinedIcon fontSize='medium' />}
      isLoading={isSubmitting || isCheckingImo}
      actions={
        <Stack direction='row' spacing={2} sx={{ pb: 1 }}>
          <Button
            variant='outlined'
            color='secondary'
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>

          {existingShip ? (
            <Button
              variant='contained'
              onClick={handleSubmit}
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={18} /> : null}
            >
              {scenarioLabel('Use Existing Ship & Update Voyage', 15)}
            </Button>
          ) : (
            <Button
              variant='contained'
              onClick={handleSubmit}
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={18} /> : null}
            >
              {scenarioLabel('Create Ship & Update Voyage', 15)}
            </Button>
          )}
        </Stack>
      }
    >
      <ScenarioModeAlert sx={{ mb: 2 }} />

      {/* ── Voyage context ── */}

      <Box
        sx={{
          mb: 2,

          p: 1.5,

          bgcolor: 'action.hover',

          borderRadius: 1,
        }}
      >
        <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 0.5 }}>
          Target Voyage
        </Typography>

        <Stack direction='row' spacing={3} flexWrap='wrap' useFlexGap>
          <ContextField label='ETD' value={formatDate(voyage.actualETD)} />

          <ContextField label='ETA' value={formatDate(voyage.actualETA)} />

          <ContextField
            label='Departure'
            value={
              voyage.departurePort?.unLocode
                ? `${voyage.departurePort.unLocode} ${voyage.departurePort.name || ''}`
                : '—'
            }
          />

          <ContextField
            label='Destination'
            value={
              voyage.destinationPort?.unLocode
                ? `${voyage.destinationPort.unLocode} ${voyage.destinationPort.name || ''}`
                : '—'
            }
          />

          <ContextField label='Remarks' value={voyage.generalRemarks || '—'} />
        </Stack>
      </Box>

      {/* ── Existing ship notice ── */}

      {existingShip && (
        <Alert severity='info' sx={{ mb: 2 }}>
          A ship with IMO {existingShip.imo} already exists (
          {existingShip.name || 'unnamed'}). You can update the voyage with this
          ship or enter a different IMO.
        </Alert>
      )}

      {/* ── Error ── */}

      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* ── Form fields ── */}

      {isCheckingImo ? (
        <Stack spacing={2} sx={{ py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontWeight: 500, mr: 0.5 }}>IMO</Typography>

            <TextField
              size='small'
              value={imoDigits}
              disabled
              sx={{ width: 160 }}
            />
          </Box>

          <Typography variant='body2' color='text.secondary'>
            Checking ship database…
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={2}>
          {/* IMO */}

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontWeight: 500, mr: 0.5 }}>IMO</Typography>

            <TextField
              size='small'
              value={imoDigits}
              onChange={handleImoChange}
              placeholder='1234567'
              inputProps={{
                maxLength: 7,

                inputMode: 'numeric',

                pattern: '[0-9]*',
              }}
              sx={{ width: 160 }}
              disabled={isSubmitting}
              helperText={
                imoDigits && imoDigits.length !== 7 ? 'Needs 7 digits' : ''
              }
              error={imoDigits.length > 0 && imoDigits.length !== 7}
            />
          </Box>

          {/* Ship Name */}

          {existingShip ? (
            <TextField
              label='Ship Name'
              size='small'
              value={existingShip.name || '—'}
              fullWidth
              disabled
              InputProps={{ readOnly: true }}
            />
          ) : (
            <TextField
              label='Ship Name'
              size='small'
              value={shipName}
              onChange={(e) => setShipName(e.target.value)}
              required
              fullWidth
              disabled={isSubmitting}
            />
          )}

          {/* Supplier */}

          {existingShip ? (
            <TextField
              label='Supplier (Shipping Company)'
              size='small'
              value={
                existingShip.supplier
                  ? `${existingShip.supplier.supplierNo} – ${existingShip.supplier.supplierName}`
                  : '—'
              }
              fullWidth
              disabled
              InputProps={{ readOnly: true }}
            />
          ) : (
            <Autocomplete
              size='small'
              options={suppliers}
              value={supplier}
              onChange={(_, v) => setSupplier(v)}
              getOptionLabel={(opt) =>
                opt ? `${opt.supplierNo} – ${opt.supplierName}` : ''
              }
              isOptionEqualToValue={(opt, val) =>
                opt?.supplierNo === val?.supplierNo
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Supplier (Shipping Company)'
                  required
                />
              )}
              disabled={isSubmitting}
            />
          )}

          {/* Optional fields — hidden when reusing existing ship */}

          {!existingShip && (
            <Stack direction='row' spacing={2}>
              <TextField
                label='IRS'
                size='small'
                value={irs}
                onChange={(e) => setIrs(e.target.value)}
                disabled={isSubmitting}
                sx={{ flex: 1 }}
              />

              <TextField
                label='Capacity'
                size='small'
                type='number'
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                disabled={isSubmitting}
                sx={{ flex: 1 }}
              />
            </Stack>
          )}
        </Stack>
      )}
    </MBModal>
  );
};

/** Small helper for displaying read-only context fields */

const ContextField = ({ label, value }) => (
  <Box sx={{ minWidth: 80 }}>
    <Typography variant='caption' color='text.secondary'>
      {label}
    </Typography>

    <Typography variant='body2'>{value || '—'}</Typography>
  </Box>
);

export default AddShipModal;
